import { apiGet } from "./apiClient";

export interface FleetDailyKpi {
  date: string;
  total_sessions: number;
  completed_sessions: number;
  completion_rate: number;
  total_acres: number;
  total_stops: number;
  stops_by_category: Record<string, number>;
  total_mowing_seconds: number;
  total_pause_seconds: number;
  avg_productivity_acres_per_hour: number;
  robots_active: number;
  interventions: number;
  acres_per_intervention: number | null;
  hours_per_intervention: number | null;
}

export interface ParetoCategory {
  category: string;
  event_category: string;
  cause_pattern: string | null;
  count: number;
  pause_seconds: number;
  percentage: number;
  cumulative_percentage: number;
}

export interface StopsParetoKpi {
  total: number;
  categories: ParetoCategory[];
}

export interface SessionKpi {
  trial_number: string;
  device_id: string;
  total_stops: number;
  error_stops_total: number;
  total_pause_seconds: number;
  stops_by_category: Record<string, number>;
  pause_seconds_by_category: Record<string, number>;
  /** null while a session is still running — no KPIs are computed until it
   *  finalises. Pair with `status === "in_progress"` to spot a live row. */
  session_outcome: string | null;
  active_session_seconds: number;
  active_mowing_seconds: number;
  rerouting_seconds: number;
  pause_seconds: number;
  idle_seconds: number;
  area_mowed_sqm: number;
  area_mowed_acres: number;
  productivity_acres_per_hour: number;
  start_time: string;
  status: string;
  software_version: string;
  address: string | null;
  interventions: number;
  acres_per_intervention: number | null;
  hours_per_intervention: number | null;
}

export interface MowerStatus {
  device_id: string;
  trial_number: string;
  last_update: string;
  latitude: number;
  longitude: number;
  gps_hdop: number;
  gps_h_accuracy: number;
  battery_charge_percent: number;
  is_ready_for_autonomy: boolean;
  has_gps_lock: boolean;
  status_message: string;
  software_version?: string | null;
  /** Session fields the endpoint already returns. `session_status` is how a
   *  device announces it is mid-run — the same field the live snapshot carries,
   *  so REST and socket agree on what "in progress" means. */
  session_status?: string | null;
  session_start_time?: string | null;
  session_end_time?: string | null;
}

function qs(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") search.set(k, String(v));
  }
  const s = search.toString();
  return s ? `?${s}` : "";
}

export function fetchFleetDaily(opts: {
  startDate?: string;
  endDate?: string;
  minDuration?: number;
  /** Comma-separated device_ids to restrict to (customer scoping). */
  deviceId?: string;
} = {}) {
  return apiGet<FleetDailyKpi[]>(
    `/kpis/fleet/daily${qs({
      start_date: opts.startDate,
      end_date: opts.endDate,
      min_duration: opts.minDuration,
      device_id: opts.deviceId,
    })}`,
  );
}

export function fetchStopsPareto(opts: {
  startDate?: string;
  endDate?: string;
  minDuration?: number;
  groupBy?: "cause" | "category";
  /** Comma-separated device_ids to restrict to (customer scoping). */
  deviceId?: string;
} = {}) {
  return apiGet<StopsParetoKpi>(
    `/kpis/stops/pareto${qs({
      start_date: opts.startDate,
      end_date: opts.endDate,
      min_duration: opts.minDuration,
      group_by: opts.groupBy ?? "cause",
      device_id: opts.deviceId,
    })}`,
  );
}

export function fetchSessionKpis(opts: {
  startDate?: string;
  endDate?: string;
  minDuration?: number;
  limit?: number;
  /** Comma-separated device_ids to restrict to (customer scoping). */
  deviceId?: string;
} = {}) {
  return apiGet<SessionKpi[]>(
    `/kpis/sessions${qs({
      start_date: opts.startDate,
      end_date: opts.endDate,
      min_duration: opts.minDuration,
      limit: opts.limit,
      device_id: opts.deviceId,
    })}`,
  );
}

export function fetchMowerStatuses() {
  return apiGet<MowerStatus[]>("/mower-status");
}
